import { PIN_REJECTED_TOAST } from '@components/community/spaceAccessCopy';
import { TOAST_WITH_DESCRIPTION_MS } from '@lib/toastDurations';
import {
  getErrorCode,
  getErrorStatus,
  JOIN_ALREADY_MEMBER_ERROR_CODE,
  JOIN_INVITE_LINK_REQUIRED_ERROR_CODE,
  JOIN_PIN_INVALID_ERROR_CODE,
  JOIN_PIN_RATE_LIMITED_ERROR_CODE,
  JOIN_REQUEST_NOT_PENDING_ERROR_CODE,
  JOIN_REQUEST_PENDING_ERROR_CODE,
} from './errorDetection';

// What a member is told when a join or a leave does not happen. One message for each thing that
// can stop it, the same words on every surface. The server's own sentence is for logs and is
// never shown. The words for a join that did happen, and the wrong-PIN words (a line under a
// PIN field, or a toast), are in src/components/community/spaceAccessCopy.ts.
//
// A refusal is read by the code in its body, and by its status alone only as the fallback for a
// backend that sends no code. Each fallback is written so that it is true in every case that
// backend answers the same way.

/**
 * What the server found in the way of a join it refused with a 409.
 *
 * - `request-pending`: the caller asked to join earlier and a host has not answered. Sent for a
 *   second join, and for a PIN sent to a Space that no longer has one. A backend before TECH-663
 *   also sent it for a correct PIN on a Space whose host reviews every request; since TECH-663
 *   (D5) that PIN is answered 201 with `status: pending`, which every PIN surface reads as a
 *   request sent, not as this conflict.
 * - `request-not-pending`: the caller sent a PIN to resolve their waiting request, and in that
 *   moment the request stopped waiting (a host declined it, or it is gone). Joining again
 *   starts a new one.
 * - `already-member`: the caller is in.
 * - `unspecified`: a 409 with no code, or with a code this client does not know. A backend that
 *   sends no code answers it for a member and for a waiting requester alike, so it is read as
 *   neither.
 */
export type JoinConflict =
  'request-pending' | 'request-not-pending' | 'already-member' | 'unspecified';

/** Which conflict a 409 from the join endpoint is, by the code in its body. */
export function classifyJoinConflict(error: unknown): JoinConflict {
  const code = getErrorCode(error);
  if (code === JOIN_REQUEST_PENDING_ERROR_CODE) return 'request-pending';
  if (code === JOIN_REQUEST_NOT_PENDING_ERROR_CODE) return 'request-not-pending';
  if (code === JOIN_ALREADY_MEMBER_ERROR_CODE) return 'already-member';
  return 'unspecified';
}

/** What a join carried besides the Space: the PIN typed for it, the invite link it came by. */
interface JoinSent {
  accessCode?: string;
  inviteToken?: string;
}

/**
 * Which of its two refusals a 403 from the join endpoint is.
 *
 * - `pin-invalid`: the PIN is wrong.
 * - `invite-link-required`: the Space is unlisted and the join carried no valid invite link.
 *   The server checks the link BEFORE it looks at the PIN, so a join that carried a PIN can be
 *   refused for its link, with a PIN that may be right.
 *
 * By the code in its body. With no code (or one this client does not know) it is inferred from
 * what was sent: a PIN was sent, so a wrong PIN; none was, so the link. The inference is wrong
 * in one case, the unlisted PIN Space above, which only the code can tell apart.
 */
function classifyJoinRefusal(
  error: unknown,
  sent: JoinSent,
): 'pin-invalid' | 'invite-link-required' {
  const code = getErrorCode(error);
  if (code === JOIN_PIN_INVALID_ERROR_CODE) return 'pin-invalid';
  if (code === JOIN_INVITE_LINK_REQUIRED_ERROR_CODE) return 'invite-link-required';
  return sent.accessCode ? 'pin-invalid' : 'invite-link-required';
}

/**
 * True when what stopped the join is a wrong PIN. Whoever sent the PIN says that itself, on the
 * line under its PIN field or in `PIN_REJECTED_TOAST` when the field is no longer on screen, so
 * useCommunityMembership stays silent for it.
 */
export function isWrongPin(error: unknown, sent: JoinSent): boolean {
  return getErrorStatus(error) === 403 && classifyJoinRefusal(error, sent) === 'pin-invalid';
}

interface MembershipToast {
  title: string;
  description?: string;
  variant?: 'error';
  duration?: number;
}

// Three of the four are news, not failures: the member is where they asked to be, or on the way
// there. `request-not-pending` is a join that did not happen and can be done again, so it is an
// error toast with the way on, kept up for 5 seconds because it has a second line to read.
const JOIN_CONFLICT_TOAST: Record<JoinConflict, MembershipToast> = {
  'request-pending': { title: 'You already have a pending request' },
  'request-not-pending': {
    title: 'Your request is no longer open',
    description: 'Join again to use your PIN.',
    variant: 'error',
    duration: TOAST_WITH_DESCRIPTION_MS,
  },
  'already-member': { title: "You're already in this Space" },
  // One line for both people a code-less 409 is sent to. It does not say to try again: the
  // same join gets the same answer.
  unspecified: { title: "You've already joined or asked to join this Space" },
};

// A failure toast: what happened, then the way on. An error toast with a second line is kept
// up for 5 seconds so that it can be read.
const failed = (whatHappened: string, wayOn: string): MembershipToast => ({
  title: whatHappened,
  description: wayOn,
  variant: 'error',
  duration: TOAST_WITH_DESCRIPTION_MS,
});

// The lockout after too many PIN attempts (every one counts, a right PIN included), which the
// backend lifts after 15 minutes: the way on is to wait, not to try again now. Passed through the
// canonical tone of voice in the PR review (TECH-663); the 15 minutes is the backend's own
// lockout, so the line promises nothing the app does not control.
const PIN_LOCKED_OUT_TOAST = failed('Too many tries', 'Try again in 15 minutes.');

// Every join failure that is not a conflict or a wrong PIN opens with the same line.
const joinFailed = (wayOn: string): MembershipToast => failed("Couldn't join this Space", wayOn);

/**
 * The one toast for a join that did not happen, by what stopped it and by what the join
 * carried (`sent`).
 *
 * - 409: which conflict it was (see `JoinConflict`).
 * - 403, the invite link (see `classifyJoinRefusal`): the Space is unlisted, and the invite
 *   link is missing or is no longer the current one. The same join gets the same answer however
 *   often it is sent, so the way on is a link from the host, not another try. Two wordings, by
 *   whether the join sent a link: one was sent, so that link stopped working and the host can
 *   give a new one; none was (a host row or a card can send this join with none, for a Space
 *   that became unlisted after it was drawn), so the Space needs one.
 * - 403, a wrong PIN: the PIN words. useCommunityMembership never asks for these (it stays
 *   silent for a wrong PIN, see `isWrongPin`); they are here so that the answer is true for
 *   whoever does ask.
 * - 429 with JOIN_PIN_RATE_LIMITED: too many PIN attempts, and the lockout lasts 15 minutes, so
 *   the way on is to wait. A 429 without that code (a gateway's limit) is the
 *   "anything else" below.
 * - 404: the Space is gone, or it is closed to this caller. The server answers both the same
 *   way and so do these words: nothing here may tell the two apart, or say why.
 * - anything else (a 5xx, a timeout, a rate limit, no connection): the join could not be
 *   served this time, and trying again can pass.
 */
export function joinFailureToast(error: unknown, sent: JoinSent): MembershipToast {
  const status = getErrorStatus(error);
  if (status === 409) return JOIN_CONFLICT_TOAST[classifyJoinConflict(error)];
  if (status === 403) {
    if (classifyJoinRefusal(error, sent) === 'pin-invalid') return PIN_REJECTED_TOAST;
    return joinFailed(
      sent.inviteToken
        ? 'This invite link no longer works. Ask the host for a new one.'
        : 'You need an invite link. Ask the host for one.',
    );
  }
  if (status === 429 && getErrorCode(error) === JOIN_PIN_RATE_LIMITED_ERROR_CODE) {
    return PIN_LOCKED_OUT_TOAST;
  }
  if (status === 404) return joinFailed("It's no longer available. Try another Space.");
  return joinFailed('Try again in a moment.');
}

/**
 * True when the server's answer to a leave is that the caller is not a member (404): they left
 * already, were removed, or the Space is gone or closed to them. That is not a leave that
 * failed and there is nothing to try again: the member wanted out and is out. So it has no
 * toast, and useCommunityMembership treats the Space as left, as after a leave that succeeded.
 */
export function isAlreadyOut(error: unknown): boolean {
  return getErrorStatus(error) === 404;
}

/**
 * The one toast for a leave that did not happen. Not asked for a 404: see `isAlreadyOut`.
 *
 * - 400: the caller is the Space's only admin, and a Space is never left without one. The way
 *   on is to make another member an admin first. Both lines say "admin", the word on the
 *   controls the toast sends the member to (member management: "Make {name} an admin?",
 *   "Promote {name} to admin", the "Admin" badge). An instruction names a control by the word
 *   on the control. Elsewhere in these words the person who lets a member in is "the host".
 * - anything else: the leave could not be served this time, and trying again can pass.
 */
export function leaveFailureToast(error: unknown): MembershipToast {
  if (getErrorStatus(error) === 400) {
    return failed(
      "You're the only admin of this Space",
      'Make another member an admin before you leave.',
    );
  }
  return failed("Couldn't leave this Space", 'Try again in a moment.');
}
